<script setup>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ref, onMounted, watch, computed } from 'vue'
import { Link, Head, usePage } from '@inertiajs/vue3'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot, Combobox, ComboboxInput, ComboboxOptions, ComboboxOption, } from '@headlessui/vue'
import { getUser, isDealer, hasPermission } from '../../Composables/userHelper'
import LoginRequests from '../../Components/LoginRequests.vue'
import NotificationsList from '../../Components/Notifications.vue'
import axios from 'axios'

const loginRequestsRef = ref(null);
const notificationsRef = ref(null);

const query = ref('')
const searchOpen = ref(false)

const searchedLots = ref([])

watch(query, (newVal) => {
    if (newVal === '') {
        searchedLots.value = []
    } else {
        axios.get('/portal/searchLots', {
            params: {
                search: newVal
            }
        }).then((response) => {
            searchedLots.value = response.data.lots
        })
    }
})

function onSelect(lot) {
    window.location = '/portal/kavel/' + lot.id
    searchOpen.value = false
    query.value = ''
}

const page = usePage();
const user = computed(() => page.props.auth.user);
const loginRequests = computed(() => page.props.login_requests);
const notifications = computed(() => page.props.notifications);
const userHasUnreadMessages = ref(false);

function checkIfUserHasUnreadMessages() {
    //get the last 5 unread notifications from user.value.notifications and then filter out the read notifications
    if (user.value && user.value.notifications) {
        const unreadNotifications = user.value.notifications.filter(notification => !notification.read);
        userHasUnreadMessages.value = unreadNotifications.length > 0;
    }
}

checkIfUserHasUnreadMessages();

const menuExpanded = ref(false)

function expandMenu(expanding) {
    menuExpanded.value = expanding
}

onMounted(() => {
    const storedState = localStorage.getItem('menuExpanded');
    if (storedState !== null) {
        menuExpanded.value = JSON.parse(storedState);
    }
});

watch(menuExpanded, (newVal) => {
    localStorage.setItem('menuExpanded', JSON.stringify(newVal));
});

const navigation = ref([
    { permission: 'none', seller: true, dealer: true, current: page.component.startsWith('Portal/Dashboard'), component: 'Portal/Dashboard', name: 'Dashboard', href: '/portal/dashboard', icon: 'house' },
    { permission: 'none', seller: false, dealer: true, current: page.component.startsWith('Portal/Search'), component: 'Portal/Search', name: 'Zoekopdrachten', href: '/portal/zoekopdrachten', icon: 'magnifying-glass' },
    { permission: 'none', seller: true, dealer: true, current: page.component.startsWith('Portal/Favorite'), component: 'Portal/Favorite', name: 'Favorieten', href: '/portal/favorieten', icon: 'heart' },
    { permission: 'none', seller: true, dealer: true, current: page.component.startsWith('Portal/Aanbod'), component: 'Portal/Aanbod', name: 'Aanbod', href: '/portal/aanbod', icon: 'car' },
    { permission: 'none', seller: true, dealer: false, current: page.component.startsWith('Portal/MyLots'), component: 'Portal/MyLots', name: 'Mijn kavels', href: '/portal/mijnkavels', icon: 'car-garage' },
    { permission: 'none', seller: false, dealer: true, current: page.component.startsWith('Portal/Bids'), component: 'Portal/Bids', name: 'Biedingen', href: '/portal/biedingen', icon: 'gavel' },
    { permission: 'dealer-index', seller: true, dealer: false, current: page.component.startsWith('Portal/Dealer'), component: 'Portal/Dealer', name: 'Dealers', href: '/portal/dealers', icon: 'car-building' },
    { permission: 'order-index', seller: true, dealer: true, current: page.component.startsWith('Portal/Invoices'), component: 'Portal/Invoices', name: 'Facturen', href: '/portal/facturen', icon: 'file-invoice' },
    { permission: 'user-index', seller: true, dealer: false, current: page.component.startsWith('Portal/Users'), component: 'Portal/Users', name: 'Verkopers', href: '/portal/gebruikers', icon: 'users' },
    { permission: 'role-index', seller: true, dealer: false, current: page.component.startsWith('Portal/Role'), component: 'Portal/Role', name: 'Rollen', href: '/portal/rollen', icon: 'user-group-crown' },
    { permission: 'lot-index', seller: true, dealer: false, current: page.component.startsWith('Portal/Listings'), component: 'Portal/Listings', name: 'Kavels', href: '/portal/kavels', icon: 'car-wrench' },
]);

const pageComponents = computed(() => page.component);
watch(pageComponents, function (component) {
    navigation.value.forEach(function (nav, i) {
        if (nav.component.includes('|')) {
            var arrayComponents = nav.component.split('|');
            var hasBeenTrue = false;
            arrayComponents.forEach(item => {
                if (!hasBeenTrue) {
                    nav.current = component.startsWith(item);
                    hasBeenTrue = nav.current;
                }
            });
        } else {
            nav.current = component.startsWith(nav.component);
        }
    })
});

const userNavigation = [
    { name: 'Instellingen', href: '/portal/settings' },
    { name: 'Uitloggen', href: '/portal/logout' },
]

const sidebarOpen = ref(false)

function shortCutSearch(e) {
    e.preventDefault();
    searchOpen.value = true;
    setTimeout(() => {
        document.getElementById('search').focus();
    }, 100);
}

document.addEventListener('keydown', function (e) {
    if (e.key === 'k' && e.ctrlKey) {
        shortCutSearch(e);
    }
});

if (hasPermission('user-accept_login')) {
    window.Echo.private('user.' + page.props.auth.user.id)
        .listen('.App\\Events\\LoginRequest', (e) => {
            loginRequestsRef.value.add(e.loginRequest);
        });
}

window.Echo.private('user.' + page.props.auth.user.id)
    .listen('.App\\Events\\NotificationEvent', (e) => {
        notificationsRef.value.add(e.notification);
    })

function canSeeNavigationItem(item) {
    //check if item dealer is true and user is dealer
    if (item.dealer && user.value.dealer) {
        return true;
    }

    //check if item seller is true and user is seller
    if (item.seller && !user.value.dealer) {
        //check if item permission is not none and user has permission
        if (item.permission !== 'none' && hasPermission(item.permission)) {
            return true;
        } else if (item.permission === 'none') {
            return true;
        }
    }

    return false;
}
</script>

<template>
    <Head>
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png">
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">
        <link rel="manifest" href="/site.webmanifest">
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#ff5a22">
        <meta name="msapplication-TileColor" content="#ff5a22">
        <meta name="theme-color" content="#ffffff">
    </Head>
    <div class="bg-white shadow z-30 fixed w-full">
        <div class="w-full px-2 sm:px-4 border lg:px-8">
            <div class="relative flex h-16 justify-between">
                <div class="relative z-10 flex px-2 lg:px-0">
                    <div class="flex flex-shrink-0 items-center gap-8">
                        <button type="button" class="-m-2.5 p-2.5 text-gray-700 xl:hidden" @click="sidebarOpen = true">
                            <span class="sr-only">Open sidebar</span>
                            <font-awesome-icon :icon="['far', 'bars']" class="text-black h-5 w-5 pt-2 shrink-0"/>
                        </button>
                        <Link class="hidden sm:block" href="/portal/dashboard">
                        <img class="h-8 w-auto xl:pl-16" src="/public/img/logo_full.svg"
                            alt="Mijn auto snel weg logo" />
                        </Link>
                    </div>
                </div>

                <div class="relative z-0 flex flex-1 items-center justify-center px-2 ">
                    <div class="w-full sm:max-w-xs" @click="searchOpen = true">
                        <label for="search" class="sr-only">Search</label>
                        <div class="relative">
                            <div class="pointer-events-none absolute top-0 bottom-0 left-0 flex items-center pl-3">
                                <font-awesome-icon :icon="['far', 'magnifying-glass']" class="text-gray-400 h-4 w-4 shrink-0"/>
                            </div>
                            <input id="search" name="search"
                                class="block w-full rounded-md border-0 bg-white py-1.5 !pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brightorange sm:text-sm sm:leading-6"
                                placeholder="Zoeken" type="search" />
                        </div>
                    </div>
                </div>
                <div class="flex items-center gap-x-4 lg:gap-x-6">
                    <Link href="/portal/notificaties"
                    :class="[active ? 'bg-gray-50' : '', 'block px-2 py-1 text-sm leading-6 text-gray-400 hover:text-gray-500']">
                    <font-awesome-icon :icon="['far', 'bell']" class="text-gray-400 h-5 w-5 pt-2 shrink-0"/> </Link>
                    <div class="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10" aria-hidden="true" />

                    <Menu as="div" class="relative">
                        <div>
                            <MenuButton
                                class="relative flex rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-brightorange focus:ring-offset-2">
                                <span class="absolute -inset-1.5" />
                                <span class="sr-only">Open user menu</span>

                                
                                <svg class="h-8 w-8 rounded-full text-gray-300 hover:text-gray-500" fill="currentColor" v-if="!user.avatar"
                                    viewBox="0 0 24 24">
                                    <path
                                        d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                                <img v-else :src="'/storage/avatars/'+user.avatar" alt="Profile picture" class="h-8 w-8 rounded-full object-cover" />
                            </MenuButton>
                        </div>
                        <transition enter-active-class="transition ease-out duration-100"
                            enter-from-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-from-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95">
                            <MenuItems
                                class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-900/5 ring-opacity-5 focus:outline-none">
                                <MenuItem v-for="item in userNavigation" :key="item.name" v-slot="{ active }">
                                <Link :href="item.href"
                                    :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700 ']">
                                {{
                                    item.name
                                }}
                                </Link>
                                </MenuItem>
                            </MenuItems>
                        </transition>
                    </Menu>
                </div>
            </div>
        </div>
    </div>
    <div>
        <TransitionRoot as="template" :show="sidebarOpen">
            <Dialog class="relative z-50" @close="sidebarOpen = false">
                <TransitionChild as="template" enter="transition-opacity ease-linear duration-300"
                    enter-from="opacity-0" enter-to="opacity-100" leave="transition-opacity ease-linear duration-300"
                    leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed left-0 right-0 top-0 bottom-0 bg-gray-900/80" />
                </TransitionChild>

                <div class="fixed left-0 right-0 top-0 bottom-0 flex">
                    <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                        enter-from="-translate-x-full" enter-to="translate-x-0"
                        leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                        leave-to="-translate-x-full">
                        <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
                            <TransitionChild as="template" enter="ease-in-out duration-300" enter-from="opacity-0"
                                enter-to="opacity-100" leave="ease-in-out duration-300" leave-from="opacity-100"
                                leave-to="opacity-0">
                                <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                                    <button type="button" class="-m-2.5 p-2.5" @click="sidebarOpen = false">
                                        <span class="sr-only">Close sidebar</span>
                                        <font-awesome-icon :icon="['far', 'xmark']" class="text-white h-5 w-5 pt-2 shrink-0"/>
                                    </button>
                                </div>
                            </TransitionChild>
                            <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4 border">
                                <div class="flex flex-row w-full justify-between">
                                    <div class="flex h-16 shrink-0 items-center">
                                        <img class="h-8 w-auto" src="/public/img/logo_full.svg" alt="mijnautosnelweg" />
                                    </div>
                                </div>
                                <nav class="flex flex-1 flex-col ">
                                    <ul role="list" class="flex flex-1 flex-col gap-y-7 ">
                                        <li>
                                            <ul role="list" class="-mx-2 space-y-1">
                                                <li v-for="item in navigation" :key="item.name">
                                                    <Link :href="item.href" @click="sidebarOpen = false" v-if="canSeeNavigationItem(item)"
                                                        :class="[item.current ? 'bg-royalblue-500 text-white' : 'text-black bg-white hover:text-white hover:bg-royalblue-500/80', 'items-center group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold']">
                                                    <font-awesome-icon :icon="['far', item.icon]" :class="[item.current ? 'text-white' : 'text-black group-hover:text-white', 'h-5 w-5 p-1 shrink-0']"/>
                                                    {{ item.name }}
                                                    </Link>
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
        <div class="lg:pl-72">
            <div
                class="sticky top-0  flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            </div>
        </div>

        <div class="flex flex-row">
            <div class="flex fixed shadow z-40">
                <div class="flex flex-col xl:w-80 h-screen bg-white" v-if="menuExpanded">
                    <div class="hidden fixed top-0 bottom-0 z-30 xl:flex w-80 flex-col">
                        <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4 border">
                            <div class="flex flex-row w-full justify-between">
                                <div class="flex h-16 shrink-0 items-center">
                                    <Link href="/portal/dashboard">
                                    <img class="h-8 w-auto" src="/public/img/logo_full.svg"
                                        alt="Mijn auto snel weg logo" />
                                    </Link>
                                </div>
                                <div class="flex ml-5 h-16 items-center">
                                    <font-awesome-icon :icon="['far', 'chevrons-left']" class="text-black h-5 w-5 shrink-0 hover:cursor-pointer"  @click="expandMenu(false)"/>
                                </div>
                            </div>
                            <nav class="flex flex-1 flex-col ">
                                <ul role="list" class="flex flex-1 flex-col gap-y-7 ">
                                    <li>
                                        <ul role="list" class="-mx-2 space-y-1">
                                            <li v-for="item in navigation" :key="item.name">
                                                <Link :href="item.href" v-if="canSeeNavigationItem(item)"
                                                    :class="[item.current ? 'bg-royalblue-500 text-white' : 'items-center text-black bg-white hover:text-white hover:bg-royalblue-500/80', 'items-center group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold']">
                                                    <font-awesome-icon :icon="['far', item.icon]" :class="[item.current ? 'text-white' : 'text-black group-hover:text-white', 'h-5 w-5 p-1 shrink-0']"/>
                                                {{ item.name }}
                                                </Link>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>

                <div class="flex flex-col h-screen bg-white" v-else>
                    <div class="hidden fixed top-0 bottom-0 z-50 xl:flex flex-col">
                        <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4 border">
                            <div class="flex flex-row w-full justify-center">
                                <div class="flex h-16 items-center">
                                    <font-awesome-icon :icon="['far', 'chevrons-right']" class="text-black h-5 w-5 shrink-0 hover:cursor-pointer"  @click="expandMenu(true)"/>
                                </div>
                            </div>
                            <nav class="flex flex-1 flex-col">
                                <ul role="list" class="flex flex-1 flex-col gap-y-7 ">
                                    <li>
                                        <ul role="list" class="-mx-2 space-y-1">
                                            <li v-for="item in navigation" :key="item.name">
                                                <Link :href="item.href" v-if="canSeeNavigationItem(item)"
                                                    :class="[item.current ? 'bg-royalblue-500 text-white' : 'text-black bg-white hover:text-white hover:bg-royalblue-500/80', 'items-center group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold']">
                                                <font-awesome-icon :icon="['far', item.icon]" :class="[item.current ? 'text-white' : 'text-black group-hover:text-white', 'h-5 w-5 p-1 shrink-0']"/>
                                                </Link>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            <main :class="menuExpanded ? 'pl-0 xl:pl-[320px] w-full' : 'pl-0 xl:pl-[74px] w-full'">
                <slot />
            </main>
        </div>

        <NotificationsList ref="notificationsRef" :notifications="notifications" />
        <LoginRequests ref="loginRequestsRef" :loginRequests="loginRequests" />
    </div>

    <TransitionRoot :show="searchOpen" as="template" @after-leave="query = ''" appear>
        <Dialog class="relative z-40" @close="searchOpen = false">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed left-0 right-0 top-0 bottom-0 bg-gray-500 bg-opacity-25 transition-opacity" />
            </TransitionChild>

            <div class="fixed left-0 right-0 top-0 bottom-0 z-40 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 scale-95"
                    enter-to="opacity-100 scale-100" leave="ease-in duration-200" leave-from="opacity-100 scale-100"
                    leave-to="opacity-0 scale-95">
                    <DialogPanel
                        class="mx-auto max-w-xl transform rounded-xl bg-white p-2 shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                        <Combobox @update:modelValue="onSelect">
                            <ComboboxInput
                                class="w-full rounded-md border-0 bg-gray-100 px-4 py-2.5 text-gray-900 focus:ring-0 sm:text-sm"
                                placeholder="Zoeken..." @change="query = $event.target.value" @blur="query = ''" />

                            <ComboboxOptions v-if="searchedLots.length > 0" static
                                class="-mb-2 max-h-72 scroll-py-2 overflow-y-auto py-2 text-sm text-gray-800">
                                <ComboboxOption v-for="lot in searchedLots" :key="lot.id" :value="lot"
                                    as="template" v-slot="{ active }">
                                    <li
                                        :class="['cursor-default select-none rounded-md px-4 py-2', active && 'bg-royalblue-500 text-white']">
                                        {{ lot.id }}
                                    </li>
                                </ComboboxOption>
                            </ComboboxOptions>

                            <div v-if="query !== '' && searchedLots.length === 0"
                                class="px-4 py-14 text-center sm:px-14">
                                <font-awesome-icon :icon="['far', 'do-not-enter']" class="text-gray-400 h-5 w-5 pt-2 shrink-0"/>
                                <p class="mt-4 text-sm text-gray-900">Geen zoekresultaten gevonden.</p>
                            </div>
                        </Combobox>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>